import { moduleEnabled } from './modules'
import type { Persona } from './personas'
import type { UsageSnapshot } from './assistantApi'

/** Whose assistant a request is: the company's (follows its package) or Platform AI. */
export type AssistantScope = 'company' | 'platform'

export const AI_ASSISTANT_MODULE = 'ai_assistant'

/** Shown instead of a generic error when the company's package leaves the assistant out. */
export const AI_NOT_IN_PACKAGE_MESSAGE = "AI assistant is not in this company's package."

/**
 * True when the in-app AI (chat button, guided prompts, AI suggestions) should be offered.
 *
 * In the Admin persona it is Platform AI: any platform admin, whatever the company in context.
 * In every other persona it is the company's AI and follows the company's package (ADR-W16:
 * a platform admin acting in a company sees what that company sees): assistant:use and the
 * AI assistant module. No modules from an older core still means every module is on.
 */
export function canUseAssistant(options: {
  hasPermission: (code: string) => boolean
  isPlatformAdmin: boolean
  activePersona: Persona | 'None'
  modules: readonly string[] | null | undefined
}): boolean {
  if (options.activePersona === 'Admin') return options.isPlatformAdmin
  return options.hasPermission('assistant:use') && moduleEnabled(options.modules, AI_ASSISTANT_MODULE)
}

/** The scope the assistant's requests carry: platform in the Admin persona, company otherwise. */
export function assistantScope(activePersona: Persona | 'None'): AssistantScope {
  return activePersona === 'Admin' ? 'platform' : 'company'
}

/** True for core's 403 {"code":"module_not_in_package"}. */
export function isModuleNotInPackage(error: unknown): boolean {
  const res = (error as { response?: { status?: number; data?: { code?: unknown; error?: unknown } } })?.response
  return res?.status === 403 && (res.data?.code === 'module_not_in_package' || res.data?.error === 'module_not_in_package')
}

/** Where a merchant manages the company's AI connection and keys. */
export const AI_SETTINGS_PLACE = 'Business › Company › AI Assistant'

const COUNT = new Intl.NumberFormat('en-US')

/** "1 Dec" from an ISO time, or null. */
function dayMonth(iso: unknown): string | null {
  if (typeof iso !== 'string' || !iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/Tallinn' })
}

/**
 * The words for core's 429 `assistant_quota_exceeded` (ADR-W22: TOU's Claude stops at the
 * allowance; an own key only at the per-minute limit).
 */
export function assistantQuotaMessage(data: {
  code?: unknown
  limit?: unknown
  limitValue?: unknown
  resetsAt?: unknown
} | null | undefined): string {
  const limit = data?.limit
  const max = typeof data?.limitValue === 'number' ? COUNT.format(data.limitValue) : null
  if (data?.code === 'ai_disabled' || limit === 'disabled') {
    return 'The AI assistant is switched off for your company.'
  }
  if (data?.code === 'ai_rate_limited' || limit === 'prompts_per_minute') {
    return 'Too many messages at once — try again in a minute.'
  }
  if (limit === 'prompts_per_day') {
    return `You've used today's AI allowance${max ? ` (${max})` : ''}. It resets tomorrow.`
  }
  if (limit === 'included_prompts_per_month') {
    const day = dayMonth(data?.resetsAt)
    return `You've used this month's AI allowance${max ? ` (${max})` : ''}. It resets${day ? ` on ${day}` : ' next month'}.`
  }
  return 'You have used your AI allowance for now.'
}

/**
 * Whether the composer should be blocked by the usage alone: the assistant switched off, or TOU's
 * daily or monthly allowance used up. Never for prompts on the company's own key (no allowance).
 */
export function usageBlocks(usage: UsageSnapshot | null | undefined): boolean {
  if (!usage) return false
  if (usage.enabled === false) return true
  if (usage.paidBy === 'company') return false
  if (usage.promptsPerDayLimit > 0 && usage.promptsToday >= usage.promptsPerDayLimit) return true
  // The monthly hard stop arrived with ADR-W22: only trust the month when core says who pays.
  return !!usage.paidBy && usage.includedPromptsPerMonth > 0 && usage.promptsThisMonth >= usage.includedPromptsPerMonth
}

/** The message for a failed assistant request: the package one for module_not_in_package. */
export function assistantErrorMessage(
  error: unknown,
  fallback: string,
  options?: { canManageKeys?: boolean },
): string {
  if (isModuleNotInPackage(error)) return AI_NOT_IN_PACKAGE_MESSAGE
  const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code
  if (code === 'ai_own_key_missing' && options?.canManageKeys === false) {
    return "Your company's AI runs on its own key, and none is active. Ask your manager to add one."
  }
  const e = error as { response?: { data?: { message?: unknown } }; message?: unknown }
  const backend = e?.response?.data?.message
  if (typeof backend === 'string' && backend.trim()) return backend
  if (typeof e?.message === 'string' && e.message.trim()) return e.message
  return fallback
}
